import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const SERVICE_IMAGES = ["/assets/svc-01.webp", "/assets/svc-02.webp", "/assets/svc-03.webp", "/assets/svc-04.webp", null, "/assets/svc-06.webp"];

export const ServicesSection = () => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get("/services")
            .then((res) => setServices(res.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    return (
        <section id="services" className="relative py-24 sm:py-32 bg-indigo-950 overflow-hidden pv-noise" data-testid="services-section">
            <div className="absolute inset-0 pv-stars opacity-30 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
                <motion.p
                    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9 }}
                    className="text-3xl sm:text-4xl tracking-[0.2em] uppercase font-semibold text-white mb-16" data-testid="services-eyebrow"
                >
                    Valeting services
                </motion.p>

                {loading ? (
                    <p className="text-blue-200 text-sm tracking-wide" data-testid="services-loading">Loading services…</p>
                ) : error ? (
                    <p className="text-blue-200 text-base font-medium" data-testid="services-error">Services couldn't load just now — please refresh the page.</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-indigo-400/25 border border-indigo-400/25" data-testid="services-grid">
                        {services.map((s, i) => (
                            <motion.div
                                key={s.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: i * 0.08 }}
                                className="group bg-indigo-900/40 hover:bg-indigo-900/70 transition-colors duration-500 hover:-translate-y-1"
                                data-testid={`service-card-${s.id}`}
                            >
                                {s.name.toLowerCase().includes("kisho") ? (
                                    <div className="bg-white">
                                        <img src="/assets/kisho-logo.png" alt="KISHO Ceramic Coatings logo" className="w-full aspect-[16/10] object-contain p-8" data-testid={`service-kisho-badge-${s.id}`} />
                                    </div>
                                ) : SERVICE_IMAGES[i] ? (
                                    <div className="bg-white">
                                        <img src={SERVICE_IMAGES[i]} alt={`${s.name} — motorhome`} className="w-full aspect-[16/10] object-cover" data-testid={`service-image-${s.id}`} />
                                    </div>
                                ) : null}
                                <div className="px-8 sm:px-10 pt-4 pb-8 sm:pb-10">
                                    <span className="block font-serif-display text-4xl font-light text-white/15 group-hover:text-blue-300/25 transition-colors duration-500 mb-2">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <h3 className="font-serif-display text-2xl sm:text-3xl tracking-tight font-semibold text-white mb-4">{s.name}</h3>
                                    <p className="text-base sm:text-lg leading-relaxed font-medium text-white/85 mb-8">{s.description}</p>
                                    <p className="text-3xl tracking-[0.1em] uppercase font-bold text-blue-300" data-testid={`service-price-${s.id}`}>{s.price}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
                <motion.p
                    initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3 }}
                    className="mt-8 text-sm text-blue-200 font-normal" data-testid="services-note"
                >
                    Final price depends on vehicle size and condition — always confirmed before work begins.
                </motion.p>
            </div>
        </section>
    );
};
